import { ISignedWaiver } from "@/interfaces/store/IBooking";
import request, { requestServices } from "@/utils/request";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import { EngineKeys } from "@/app-search/services/request-app-search.service";

const WAIVER_INDEX = import.meta.env.VITE_ELASTIC_WAIVERS;
export const WAIVER_PAGE_SIZE = 10;

// In mapHitToWaiver:
function mapHitToWaiver(source: Record<string, any>): ISignedWaiver {
  return {
    waiverId: String(source.waiver_id),
    customerName: source.waiver_signatory_full_name || "",
    signedAt: source.waiver_created_at || "",
    waiverTitle: source.waiver_template_title || "",
    numberOfDependents: source.waiver_dependents_count ?? null,
  };
}

/**
 * Fetch signed waivers for the group from Elasticsearch.
 * Supports optional search query on waiver_signatory_full_name, waiver_id,
 * and waiver_template_title. Supports pagination via `from`.
 */
export async function getSignedWaivers(
  query?: string,
  from = 0
): Promise<{ waivers: ISignedWaiver[]; total: number }> {
  const groupId = POSConfigModule.groupId;
  const token = UserModule?.getUser?.elasticSearchTokens?.[EngineKeys.Waiver];

  const esPayload = query
    ? {
        query: {
          bool: {
            must: [
              {
                query_string: {
                  query: `*${query}*`,
                  fields: [
                    "waiver_signatory_full_name",
                    "waiver_template_title",
                  ],
                  default_operator: "AND",
                },
              },
            ],
            filter: [{ term: { group_access: groupId } }],
          },
        },
        from,
        size: WAIVER_PAGE_SIZE,
      }
    : {
        query: {
          bool: {
            filter: [{ term: { group_access: groupId } }],
          },
        },
        from,
        size: WAIVER_PAGE_SIZE,
      };

  const response = await requestServices({
    method: "POST",
    url: `/v1/search/${WAIVER_INDEX}`,
    data: esPayload,
    headers: { Authorization: `Bearer ${token}` },
  });

  const hits: Record<string, any>[] = response.data?.data?.hits?.hits || [];
  const total: number = response.data?.data?.hits?.total?.value || 0;

  return {
    waivers: hits.map((hit) => mapHitToWaiver(hit._source)),
    total,
  };
}

/**
 * Fetch count of signed waivers linked to a booking.
 * GET /booking/{bookingId}/details/organiser
 */
export async function getLinkedWaiversCount(
  bookingId: number
): Promise<number> {
  const response = await request({
    method: "GET",
    url: `/booking/${bookingId}/details/organiser`,
  });
  const signedWaivers: any[] = response.data?.signedWaivers || [];
  return signedWaivers.length;
}

/**
 * Fetch active waiver template version id for the group.
 * GET /waivers?groupId={groupId}
 * Returns the activeVersion.id of the template with status: 1
 */
export async function getActiveWaiverTemplateVersionId(): Promise<
  number | null
> {
  const groupId = POSConfigModule.groupId;
  const response = await requestServices({
    method: "GET",
    url: `/waivers`,
    params: { groupId },
  });

  const templates: Record<string, any>[] = response.data?.data || [];
  const activeTemplate = templates.find((t) => t.status === 1);
  return activeTemplate?.activeVersion?.id ?? null;
}

/**
 * Link a signed waiver to a booking.
 * POST /signed-waivers/{id}/bookings
 */
export async function linkWaiverToBooking(
  waiverId: string,
  bookingId: number
): Promise<void> {
  await requestServices({
    method: "POST",
    url: `/signed-waivers/${waiverId}/bookings`,
    data: { bookingId },
  });
}

/**
 * Unlink a signed waiver from a booking.
 * DELETE /signed-waivers/{id}/bookings/{bookingId}
 */
export async function unlinkWaiverFromBooking(
  waiverId: string,
  bookingId: number
): Promise<void> {
  await requestServices({
    method: "DELETE",
    url: `/signed-waivers/${waiverId}/bookings/${bookingId}`,
  });
}
